import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Checkbox,
  Button,
  notification,
  Spin,
  Input,
  Select,
} from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchUsersDirectory, inviteUser } from "../services/projectsAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import ProjectSettingToggle from "./ProjectSettingToggle";

const AddUsersFromDirectoryModal = ({ visible, onClose }) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [userRoles, setUserRoles] = useState({});
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);
  const getUserId = (user) => user.id || user._id;
  
  // Fetch all users from directory (all projects including current)
  const { data: directoryData, isLoading } = useQuery({
    queryKey: ["usersDirectory", projectId],
    queryFn: () => fetchUsersDirectory(projectId),
    enabled: visible && !!projectId,
  });
  const allUsers = directoryData?.data || [];

  const availableUsers = useMemo(() => {
    return allUsers.filter((user) => !user.is_already_member);
  }, [allUsers]);

  // Filter users based on search text
  const filteredUsers = useMemo(() => {
    if (!searchText) return allUsers;
    const searchLower = searchText.toLowerCase();
    return allUsers.filter(
      (user) =>
        (user.first_name?.toLowerCase().includes(searchLower) ||
          user.last_name?.toLowerCase().includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.vendor_name?.toLowerCase().includes(searchLower) ||
          user.company_name?.toLowerCase().includes(searchLower))
    );
  }, [allUsers, searchText]);

  const alreadyMemberIds = useMemo(() => {
    return allUsers
      .filter((user) => user.is_already_member)
      .map(getUserId);
  }, [allUsers]);

  const usersToInvite = useMemo(() => {
    const seenEmails = new Set();
    return selectedUserIds.filter((userId) => {
      const user = allUsers.find((u) => getUserId(u) === userId);
      if (!user || user.is_already_member) return false;
      const email = (user.email || "").toLowerCase();
      if (email && seenEmails.has(email)) return false;
      if (email) seenEmails.add(email);
      return true;
    });
  }, [selectedUserIds, allUsers]);

  useEffect(() => {
    if (alreadyMemberIds.length > 0) {
      setSelectedUserIds((prev) => [...new Set([...prev, ...alreadyMemberIds])]);
    }
  }, [alreadyMemberIds]);


  useEffect(() => {
    if (!visible) {
      setSelectedUserIds([]);
      setUserRoles({});
      setSendInvitationEmail(true);
    }
  }, [visible]);

  const getSelectedRoleForUser = (userId) => {
    const user = allUsers.find((u) => getUserId(u) === userId);
    if (!user) return "subcontractor";
    return userRoles[userId] || getDefaultRole(user);
  };

  const inviteMutation = useMutation({
    mutationFn: async (userIds) => {
      const results = await Promise.allSettled(
        userIds.map(async (userId) => {
          const user = allUsers.find((u) => getUserId(u) === userId);
          if (!user || user.is_already_member) return null;

          const selectedRole = getSelectedRoleForUser(userId);
          await inviteUser({
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            project_id: projectId,
            role: selectedRole,
            vendor_id: user.vendor_id || null,
            send_invitation_email: sendInvitationEmail,
          });
          return user;
        })
      );

      const successful = [];
      const failed = [];

      results.forEach((result, index) => {
        const userId = userIds[index];
        const user = allUsers.find((u) => getUserId(u) === userId);

        if (result.status === 'fulfilled' && result.value) {
          successful.push(result.value);
        } else if (result.status === 'rejected' && user && !user.is_already_member) {
          const errorDetail = result.reason?.response?.data?.detail;
          const error = typeof errorDetail === 'string' 
            ? errorDetail 
            : errorDetail?.message || result.reason?.message || 'Failed to invite user';
          failed.push({ user, error });
        }
      });

      return { successful, failed };
    },
    onSuccess: ({ successful, failed }) => {
      if (failed.length === 0) {
        notification.success({
          message: sendInvitationEmail
            ? "Users invited successfully"
            : "Users added successfully",
          description: `${successful.length} user(s) have been ${
            sendInvitationEmail ? "invited to" : "added to"
          } the project.`,
        });
        queryClient.invalidateQueries(["projectUsers", projectId]);
        queryClient.invalidateQueries(["vendors", projectId]);
        handleClose();
      } else if (successful.length === 0) {
        const errors = failed.map(f => `${f.user.email}: ${f.error}`).join(', ');
        notification.error({
          message: sendInvitationEmail
            ? "Failed to invite users"
            : "Failed to add users",
          description: `All ${sendInvitationEmail ? "invitation(s)" : "addition(s)"} failed. ${errors}`,
          duration: 8,
        });
      } else {
        const errors = failed.map(f => `${f.user.email}: ${f.error}`).join('; ');
        notification.warning({
          message: "Partial success",
          description: `${successful.length} user(s) ${
            sendInvitationEmail ? "invited" : "added"
          } successfully, ${failed.length} failed. ${errors}`,
          duration: 8,
        });
        queryClient.invalidateQueries(["projectUsers", projectId]);
        queryClient.invalidateQueries(["vendors", projectId]);
      }
    },
    onError: (error) => {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : errorDetail?.message || error.message || "An unknown error occurred";
      notification.error({
        message: sendInvitationEmail
          ? "Failed to invite users"
          : "Failed to add users",
        description: errorMessage,
      });
    },
  });

  const handleSelectAll = () => {
    const filteredAvailableUserIds = filteredUsers
      .filter((user) => !user.is_already_member)
      .map(getUserId);
    const allAvailableSelected = filteredAvailableUserIds.every((id) => selectedUserIds.includes(id));

    if (allAvailableSelected) {
      setSelectedUserIds((prev) => 
        prev.filter((id) => !filteredAvailableUserIds.includes(id))
      );
    } else {
      setSelectedUserIds((prev) => [...new Set([...prev, ...alreadyMemberIds, ...filteredAvailableUserIds])]);
    }
  };

  const handleToggleUser = (userId) => {
    const user = allUsers.find((u) => getUserId(u) === userId);
    if (user?.is_already_member) return;
    
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleRoleChange = (userId, role) => {
    setUserRoles((prev) => ({
      ...prev,
      [userId]: role,
    }));
  };

  const handleInvite = () => {
    if (usersToInvite.length === 0) {
      notification.warning({
        message: "No users selected",
        description: "Please select at least one user to invite (users already in the project cannot be invited).",
      });
      return;
    }
    inviteMutation.mutate(usersToInvite);
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setSearchText("");
    setUserRoles({});
    setSendInvitationEmail(true);
    onClose();
  };

  const getUserDisplayInfo = (user) => {
    const role = user.formatted_role || user.role || "N/A";
    const company = user.company_name || user.vendor_name;
    return company ? `${role} • (${company})` : role;
  };

  // Get default role for a user - if user is owner in another project, default to project_manager
  const getDefaultRole = (user) => {
    const userRole = user.role;
    if (userRole === "owner") {
      return "project_manager";
    }
    return userRole || "subcontractor";
  };

  // if no project is selected or projectId is missing, do not render the modal
  if (!selectedProject || !projectId) {
    return null;
  }

  return (
    <Modal
      title={`${sendInvitationEmail ? "Invite" : "Add"} Users to ${selectedProject?.project_name || "Project"}`}
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      width={600}
    >
      <div className="mb-4">
        <div className="flex justify-start items-center mb-3">
          <a
            onClick={handleSelectAll}
            className="text-blue-600 hover:text-blue-800 cursor-pointer"
          >
            {filteredUsers.filter((user) => !user.is_already_member).length > 0 &&
            filteredUsers
              .filter((user) => !user.is_already_member)
              .every((user) => selectedUserIds.includes(getUserId(user)))
              ? "Deselect All"
              : "Select All"}
          </a>
        </div>
      </div>

      <div className="mb-4">
        <Input.Search
          placeholder="Search users by name, email, vendor name..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      <div
        style={{
          maxHeight: "400px",
          overflowY: "auto",
          border: "1px solid #d9d9d9",
          borderRadius: "4px",
          padding: "8px",
        }}
      >
        {isLoading ? (
          <div className="text-center py-8">
            <Spin size="large" />
            <p className="mt-4">Loading users...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchText ? "No users found matching your search." : "No users found in your projects."}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredUsers.map((user) => {
              const userId = getUserId(user);
              const isSelected = selectedUserIds.includes(userId);
              const isAlreadyMember = user.is_already_member;

              return (
                <div
                  key={userId}
                  className={`flex items-start p-2 rounded ${
                    isAlreadyMember
                      ? "opacity-60 cursor-not-allowed bg-gray-100"
                      : "hover:bg-gray-50 cursor-pointer"
                  }`}
                  onClick={() => !isAlreadyMember && handleToggleUser(userId)}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={isAlreadyMember}
                    onChange={(e) => {
                      e.stopPropagation();
                      if (!isAlreadyMember) {
                        handleToggleUser(userId);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="mr-3 mt-1"
                  />
                  <div className="flex-1">
                    <div className="font-medium">
                      {user.first_name} {user.last_name}
                      {user.email && (
                        <span className="ml-1 text-gray-500 font-normal">
                          ({user.email})
                        </span>
                      )}
                      {isAlreadyMember && (
                        <span className="ml-2 text-xs text-gray-500 font-normal">
                          (Already in project)
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {getUserDisplayInfo(user)}
                    </div>
                  </div>
                  {!isAlreadyMember && (
                    <div className="ml-2" onClick={(e) => e.stopPropagation()}>
                      <Select
                        placeholder="Role"
                        value={getSelectedRoleForUser(userId)}
                        onChange={(value) => handleRoleChange(userId, value)}
                        style={{ width: 150 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Select.Option value="project_manager">Project Manager</Select.Option>
                        <Select.Option value="field">Field</Select.Option>
                        <Select.Option value="consultant">Consultant</Select.Option>
                        <Select.Option value="subcontractor">Subcontractor</Select.Option>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-4">
        <ProjectSettingToggle
          title="Send Invitation Email"
          checked={sendInvitationEmail}
          onChange={setSendInvitationEmail}
        />
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleInvite}
            loading={inviteMutation.isPending}
            disabled={usersToInvite.length === 0}
          >
            {sendInvitationEmail
              ? `Invite Users (${usersToInvite.length})`
              : `Add Users (${usersToInvite.length})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddUsersFromDirectoryModal;
